import { apiClient } from '../api/apiClient';
import { DIFFICULTY_LEVELS_ARRAY, ANSWER_OPTIONS_ARRAY, type DifficultyLevel, type AnswerOption } from '@/constants/assessment';
import type { ApiResponse } from '@/types/assessment';

export interface DifficultyInfo {
  value: DifficultyLevel;
  label: string;
  description: string;
  color: 'success' | 'warning' | 'error';
  order: number;
}

export interface AnswerOptionInfo {
  value: AnswerOption;
  label: string;
}

export interface DifficultyStatistics {
  difficulty: DifficultyLevel;
  totalQuestions: number;
  totalAssessments: number;
  averageScore: number;
  completionRate: number;
}

class DifficultyService {
  private readonly baseUrl = '/assessment/enums';

  /**
   * Get all difficulty levels with metadata
   * GET /api/assessment/enums/difficulties
   */
  async getDifficulties(): Promise<ApiResponse<{ difficulties: DifficultyInfo[] }>> {
    return apiClient.get(`${this.baseUrl}/difficulties`);
  }

  /**
   * Get difficulty statistics
   * GET /api/assessment/enums/difficulties/statistics
   */
  async getDifficultyStatistics(): Promise<ApiResponse<{ statistics: DifficultyStatistics[] }>> {
    return apiClient.get(`${this.baseUrl}/difficulties/statistics`);
  }

  /**
   * Get answer options
   * GET /api/assessment/enums/answer-options
   */
  async getAnswerOptions(): Promise<ApiResponse<{ options: AnswerOptionInfo[] }>> {
    return apiClient.get(`${this.baseUrl}/answer-options`);
  }

  /**
   * Get question order options
   * GET /api/assessment/enums/question-orders
   */
  async getQuestionOrderOptions(): Promise<ApiResponse<{ options: Array<{ value: string; label: string; description: string }> }>> {
    return apiClient.get(`${this.baseUrl}/question-orders`);
  }

  /**
   * Get all enums in one call
   * GET /api/assessment/enums/all
   */
  async getAllEnums(): Promise<ApiResponse<{
    difficulties: DifficultyInfo[];
    answerOptions: AnswerOptionInfo[];
    questionOrders: Array<{ value: string; label: string; description: string }>;
  }>> {
    return apiClient.get(`${this.baseUrl}/all`);
  }

  // Client-side utility methods for fallback
  
  /**
   * Get difficulty levels (client-side fallback)
   */
  getDifficultyLevels(): DifficultyInfo[] {
    return [
      {
        value: 'Beginner',
        label: 'Beginner',
        description: 'Basic level questions suitable for entry-level candidates',
        color: 'success',
        order: 1,
      },
      {
        value: 'Intermediate',
        label: 'Intermediate',
        description: 'Moderate difficulty questions for experienced candidates',
        color: 'warning',
        order: 2,
      },
      {
        value: 'Advanced',
        label: 'Advanced',
        description: 'Complex questions for senior-level candidates',
        color: 'error',
        order: 3,
      },
    ];
  }

  /**
   * Get answer options (client-side fallback)
   */
  getAnswerOptionsLocal(): AnswerOptionInfo[] {
    return ANSWER_OPTIONS_ARRAY.map(option => ({
      value: option,
      label: `Option ${option}`,
    }));
  }

  /**
   * Get difficulty color for UI components
   */
  getDifficultyColor(difficulty: DifficultyLevel): 'success' | 'warning' | 'error' | 'default' {
    switch (difficulty) {
      case 'Beginner':
        return 'success';
      case 'Intermediate':
        return 'warning';
      case 'Advanced':
        return 'error';
      default:
        return 'default';
    }
  }

  /**
   * Get difficulty order for sorting
   */
  getDifficultyOrder(difficulty: DifficultyLevel): number {
    switch (difficulty) {
      case 'Beginner':
        return 1;
      case 'Intermediate':
        return 2;
      case 'Advanced':
        return 3;
      default:
        return 0;
    }
  }

  /**
   * Validate difficulty level
   */
  isValidDifficulty(difficulty: string): difficulty is DifficultyLevel {
    return DIFFICULTY_LEVELS_ARRAY.includes(difficulty as DifficultyLevel);
  }

  /**
   * Validate answer option
   */
  isValidAnswerOption(option: string): option is AnswerOption {
    return ANSWER_OPTIONS_ARRAY.includes(option as AnswerOption);
  }
}

export const difficultyService = new DifficultyService();